import {
  OpenQuestionStudentPin,
  OpenQuestionTeacherPin,
  Pin,
  PollStudentPin,
  PollTeacherPin,
  QuizPin,
  QuizStudentPin,
  QuizTeacherPin,
  SlidePin,
  SlideStudentPin,
  SlideTeacherPin,
  TeacherPin,
  WordCloudStudentPin,
  WordCloudTeacherPin,
  WordCloudPin,
  PinType,
  TeacherPinSchema,
  PinComponent,
  TextPinComponent,
  SortingQuestionPin,
  SortingQuestionTeacherPin,
  SortingQuestionStudentPin,
  DragAndDropPin,
  DragAndDropTeacherPin,
  DragAndDropStudentPin,
} from '../../models';
import { OpenQuestionPin } from '../../models/pin/open-question-pin';
import { PollPin } from '../../models/pin/poll-pin';
import { emptyDocument } from '../../tip-tap';
import { isInteractiveQuestionPinTextPinComponent } from '../assertions';
import { processPinReferenceFields } from '../component-reference.processor';
import { defaultGroup, getPinOrderGenerator, orderableCompareFn } from '../order';
import { clonePinComponents } from '../pin-components/pin-component';
import { randomId } from '../random-id';
import { PinCreationParams } from './pin-helper-types';

export type PinTypeToPin<T extends PinType> = T extends 'MULTIPLE_CHOICE'
  ? QuizPin
  : T extends 'SLIDE'
    ? SlidePin
    : T extends 'OPEN_QUESTION'
      ? OpenQuestionPin
      : T extends 'WORD_CLOUD'
        ? WordCloudPin
        : T extends 'POLL'
          ? PollPin
          : T extends 'SORTING_QUESTION'
            ? SortingQuestionPin
            : T extends 'DRAG_AND_DROP'
              ? DragAndDropPin
              : never;

export type PinTypeToTeacherPin<T extends PinType> = T extends 'MULTIPLE_CHOICE'
  ? QuizTeacherPin
  : T extends 'SLIDE'
    ? SlideTeacherPin
    : T extends 'OPEN_QUESTION'
      ? OpenQuestionTeacherPin
      : T extends 'WORD_CLOUD'
        ? WordCloudTeacherPin
        : T extends 'POLL'
          ? PollTeacherPin
          : T extends 'SORTING_QUESTION'
            ? SortingQuestionTeacherPin
            : T extends 'DRAG_AND_DROP'
              ? DragAndDropTeacherPin
              : never;

export type PinSettings<T extends PinType> = PinTypeToPin<T>['settings'];

export type PinTeacherSettings<T extends PinType> = PinTypeToTeacherPin<T>['teacherSettings'];

export type PinSettingsLayout<T extends PinType> = PinSettings<T>['layout'];

export type PinTypeToStudentPin<T extends PinType> = T extends 'MULTIPLE_CHOICE'
  ? QuizStudentPin
  : T extends 'SLIDE'
    ? SlideStudentPin
    : T extends 'OPEN_QUESTION'
      ? OpenQuestionStudentPin
      : T extends 'WORD_CLOUD'
        ? WordCloudStudentPin
        : T extends 'POLL'
          ? PollStudentPin
          : T extends 'SORTING_QUESTION'
            ? SortingQuestionStudentPin
            : T extends 'DRAG_AND_DROP'
              ? DragAndDropStudentPin
              : never;

export type PinStudentSettings<T extends PinType> = PinTypeToStudentPin<T>['studentSettings'];

export function newPin(pin: PinCreationParams, id?: string, order?: string): TeacherPin {
  return TeacherPinSchema.parse({
    ...pin,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    id: id ?? randomId(),
    order: order ?? 'defaultPinCreationOrder', // will be updated in addPinToList in reducer
    teacherSettings: {
      ...pin.teacherSettings,
      notes: pin.teacherSettings.notes || emptyDocument(),
    },
  });
}

export function sortPins<T extends Pin>(components: T[]): T[] {
  return [...components].sort(orderableCompareFn);
}

export function clonePins(pins: TeacherPin[]): TeacherPin[] {
  return pins.map(clonePin);
}

/**
 * Sets the initial order of the pins to ensure they stay in the correct order after cloning.
 * @param sortedPins - The pins to set the initial order of, already sorted.
 */
export function setInitialOrder(sortedPins: TeacherPin[]): TeacherPin[] {
  const orders = getPinOrderGenerator([]).nKeysStart(sortedPins.length, defaultGroup);
  return sortedPins.map((pin, i) => ({ ...pin, order: orders[i] }));
}

export function clonePin(pin: TeacherPin): TeacherPin {
  const { pinComponents, oldToNewPinComponentIds } = clonePinComponents(pin.pinComponents);

  const newPin: TeacherPin = {
    ...pin,
    id: randomId(),
    pinComponents,
  };

  processPinReferenceFields(
    pin,
    ({ componentId }) => {
      return oldToNewPinComponentIds[componentId];
    },
    { ignorePinComponents: true }
  );

  return newPin;
}

export const findQuestionComponent = (pin: Pin, pinComponents: PinComponent[]) => {
  return pinComponents.find((pinComponent): pinComponent is TextPinComponent =>
    isInteractiveQuestionPinTextPinComponent(pin, pinComponent)
  );
};
